import React, { useMemo, useState } from 'react';
/**
 * ONE MARK, THREE SOURCES.
 *
 * The catalog returns a `logo_url` per integration, but it is nullable and the
 * hosts behind it are not all reliable — so both the index and the detail page
 * were falling straight to an orange initial-letter square. A wall of letter
 * tiles is the single loudest way for a directory to look unfinished, and it
 * was happening for exactly the long tail of tools a visitor is most likely to
 * be searching for.
 *
 * So the mark is resolved in order, each step only used when the one before it
 * fails to load:
 *
 *   1. the catalog's own `logo_url` — authoritative when present;
 *   2. Simple Icons, keyed on the slug — covers most well-known SaaS brands;
 *   3. Dashboard Icons, same key — covers self-hosted and infra tools that
 *      Simple Icons does not carry;
 *   4. the initial, which is now genuinely a last resort rather than the
 *      second option.
 *
 * The slug is normalised before it is used as a key: catalog slugs carry
 * qualifiers ("google-drive-oauth", "postgres_db") that neither icon host
 * knows, and trimming them is the difference between a hit and a letter.
 */
/**
 * Giga's own mark. It is on no icon CDN, so it cannot come through the
 * fallback chain below — every lockup that pairs a partner with Giga needs it
 * by hand, and having it in one place keeps those pairings identical.
 */
export const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png';
const ICON_HOSTS = [(key: string) => `https://cdn.simpleicons.org/${key}`, (key: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${key}.svg`];

/**
 * MARKS THE FALLBACK CHAIN CANNOT FIND, PINNED BY HAND.
 *
 * The chain below survives a 404 — it moves to the next host — but it cannot
 * recover from a host that answers with something that is not the brand's
 * mark, and it cannot invent a mark that no host carries. Simple Icons has
 * removed several logos on trademark grounds (Intercom among them), so those
 * slugs fall all the way through to an orange initial-letter tile: the exact
 * "unfinished directory" failure the chain exists to prevent, and visible on
 * the connections map and every exported asset that names the tool.
 *
 * Anything listed here is resolved FIRST, ahead of the hosts. Add a slug when
 * you see a letter tile where a logo should be.
 */
const LOGO_OVERRIDES: Record<string, string> = {
  intercom: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/intercom.svg',
  // Teams' Simple Icons entry was withdrawn too, and the slug varies by source.
  teams: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg',
  microsoftteams: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg',
  'microsoft-teams': 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg'
};

/** Strip connection-method and product qualifiers the icon hosts don't know. */
function iconKey(slug: string): string {
  return slug.toLowerCase().replace(/_/g, '-').replace(/-(oauth2?|api|apikey|api-key|mcp|pat|token|db|database|cloud|server|app)$/g, '').trim();
}
export function IntegrationLogo({
  slug,
  name,
  logoUrl,
  className = 'size-8',
  rounded = 'rounded-lg'
}: {
  slug: string;
  name: string;
  logoUrl?: string | null;
  /** Sizing utilities for the mark. */
  className?: string;
  rounded?: string;
}) {
  const sources = useMemo(() => {
    const key = iconKey(slug);
    // An explicit `logoUrl` from the catalog still wins — it is authoritative
    // for that integration — then the hand-pinned overrides, then the hosts.
    return [logoUrl, LOGO_OVERRIDES[key], ...ICON_HOSTS.map(build => build(key))].filter(Boolean) as string[];
  }, [slug, logoUrl]);
  const [index, setIndex] = useState(0);
  const src = sources[index];
  if (!src) {
    return <span aria-hidden="true" className={`${className} ${rounded} flex flex-none items-center justify-center bg-[#F1EAE0] text-[13px] font-bold text-[#8A7B6D]`} data-id="element-4293" data-mp="e7xxycdp4njv">
        {name.charAt(0).toUpperCase()}
      </span>;
  }
  return <img
  // Keyed on the source so a failed load actually remounts the element
  // rather than leaving the browser's broken-image state in place.
  key={src} src={src} alt="" aria-hidden="true" loading="lazy" onError={() => setIndex(i => i + 1)} className={`${className} ${rounded} flex-none object-contain`} data-id="element-4294" data-mp="onp1021oyrap" />;
}